import { z } from 'zod';
import { e164Schema } from './phone';

/**
 * What a finished call left undone, as FACTS: the office's half of a call's
 * result (ADR-318 P13 gives the campaign `CallResult`; this is the other half).
 *
 * Built by the voice worker at teardown from the walk's final state, sent to
 * `POST /internal/interactions/:id/complete` as a top-level `followUps`, beside
 * `callResult` and never inside it. `buildCallResult` drops a whole result on
 * any issue, and a refused `CallResult` must never take a patient's callback
 * with it.
 *
 * FACTS ONLY. No titles, priorities, labels or routing: whether a fact becomes
 * a new task, a note on the call's own task, or nothing is the API's decision
 * (`decideCallFollowUps`), made once and after the campaign has acted.
 *
 * EVERY FIELD IS NULLABLE AND CHECKED ON ITS OWN. A field that fails its schema
 * is nulled and named in `unreadable` by {@link salvageCallFollowUpFacts},
 * which the worker runs before it sends and the API runs again on receipt, so
 * one malformed fact costs that fact and nothing else.
 *
 * PHI: these carry what the caller said (a message, an ask, a callback's
 * words). They are stored on the interaction; logs carry field names only.
 */

/** 24-hour clinic-local `HH:MM`, the same rule as `requestedCallbackSchema.exactTime`. */
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** One thing the caller raised, as `AnythingElse` and the parked queue hold it. */
export const callAskSchema = z.object({
  topic: z.string().min(1).max(200),
  details: z.string().max(2000),
});
export type CallAsk = z.infer<typeof callAskSchema>;

/**
 * Where the callback number came from.
 *
 *   stated         the caller said a number (agent-core `CallbackNumberSource`)
 *   this_line      the line the call is already on (agent-core `CallbackNumberSource`)
 *   line_fallback  a deferral with no number: the worker used the call's line
 *   none           no usable number at all (withheld caller ID, a browser call)
 */
export const FOLLOW_UP_NUMBER_SOURCES = ['stated', 'this_line', 'line_fallback', 'none'] as const;
export type FollowUpNumberSource = (typeof FOLLOW_UP_NUMBER_SOURCES)[number];

/**
 * How the assistant failed, as the worker saw it. `worker_lost` is not here:
 * a call whose worker died sends nothing, and the API files that one itself.
 */
export const CALL_FAILURE_REASONS = [
  /** The session body threw, setup included. */
  'session_error',
  /** The workflow walk rejected. */
  'workflow_threw',
  /** No workflow could be built for the call. */
  'no_workflow',
  /** The walk stalled: step cap, supervisor stall, or a node that never finished. */
  'workflow_stalled',
  /** The call hit the session's duration cap. */
  'max_duration',
] as const;
export type CallFailureReason = (typeof CALL_FAILURE_REASONS)[number];

export const callFollowUpCallbackSchema = z.object({
  number: e164Schema.nullable(),
  numberSource: z.enum(FOLLOW_UP_NUMBER_SOURCES),
  date: z.string().date().nullable(),
  time: z.string().regex(HHMM).nullable(),
  timezone: z.string().max(64).nullable(),
  whenSaid: z.string().max(200).nullable(),
});

export const callFollowUpMessageSchema = z.object({
  /** `take_message`: TakeMessage captured it. `handoff`: a transfer promised a call back. */
  origin: z.enum(['take_message', 'handoff']),
  topic: z.string().max(200).nullable(),
  details: z.string().max(2000).nullable(),
  callbackPromised: z.boolean(),
});

export const callFollowUpAsksSchema = z.object({
  /** Heard out and recorded at wrap-up (`AnythingElse.record_ask`). */
  recorded: z.array(callAskSchema).max(10),
  /** Still on the parked queue at close: nobody got to them. */
  unanswered: z.array(callAskSchema).max(10),
});

export const callFollowUpAppointmentSchema = z.object({
  wanted: z.enum(['schedule', 'reschedule']),
  /** The scheduling step's own outcome, or null when it never ran. */
  scheduleOutcome: z.string().max(64).nullable(),
  slotsOffered: z.number().int().min(0).max(50),
});

export const callFollowUpFailureSchema = z.object({
  reason: z.enum(CALL_FAILURE_REASONS),
  /** Where the walk was when it failed, when known. */
  lastNodeId: z.string().max(100).nullable(),
});

/**
 * Counts and one enum, never content. Lets the API tell an abandoned or
 * one-sided call from a normal short one, and is the monitoring for a session
 * the SDK closed under us (`endReason: 'session_closed'`, plan L1).
 */
export const callFollowUpShapeSchema = z.object({
  secondsOnLine: z.number().int().min(0),
  callerTurns: z.number().int().min(0),
  agentLines: z.number().int().min(0),
  /** The first hang-up reason (`HANGUP_REASON` in the voice worker). */
  endReason: z.string().max(40).nullable(),
});

/** The fields salvage can null one by one. */
export const CALL_FOLLOW_UP_FACT_FIELDS = [
  'verifiedPatientId',
  'line',
  'callback',
  'message',
  'asks',
  'appointment',
  'failure',
  'shape',
  'excerpt',
] as const;
export type CallFollowUpFactField = (typeof CALL_FOLLOW_UP_FACT_FIELDS)[number];

const FIELD_SCHEMAS = {
  /** Set only when identity was verified on the call. */
  verifiedPatientId: z.string().uuid().nullable(),
  /** The line the call was on: the caller's ANI inbound, the dialled number outbound. */
  line: e164Schema.nullable(),
  callback: callFollowUpCallbackSchema.nullable(),
  message: callFollowUpMessageSchema.nullable(),
  asks: callFollowUpAsksSchema.nullable(),
  appointment: callFollowUpAppointmentSchema.nullable(),
  failure: callFollowUpFailureSchema.nullable(),
  shape: callFollowUpShapeSchema.nullable(),
  /**
   * The end of the conversation, for whoever picks the work up: the last few
   * turns, labelled by speaker. Null when nothing was said.
   */
  excerpt: z.string().max(2000).nullable(),
} satisfies Record<CallFollowUpFactField, z.ZodType>;

export const callFollowUpFactsSchema = z.object({
  ...FIELD_SCHEMAS,
  /** Fields that failed validation and were nulled, by the worker or the API. */
  unreadable: z.array(z.enum(CALL_FOLLOW_UP_FACT_FIELDS)).max(CALL_FOLLOW_UP_FACT_FIELDS.length),
});
export type CallFollowUpFacts = z.infer<typeof callFollowUpFactsSchema>;
export type CallFollowUpCallback = z.infer<typeof callFollowUpCallbackSchema>;
export type CallFollowUpMessage = z.infer<typeof callFollowUpMessageSchema>;
export type CallFollowUpAsks = z.infer<typeof callFollowUpAsksSchema>;
export type CallFollowUpAppointment = z.infer<typeof callFollowUpAppointmentSchema>;
export type CallFollowUpFailure = z.infer<typeof callFollowUpFailureSchema>;
export type CallFollowUpShape = z.infer<typeof callFollowUpShapeSchema>;

/** Facts with nothing in them: a call that left nothing undone. */
export function emptyCallFollowUpFacts(): CallFollowUpFacts {
  return {
    verifiedPatientId: null,
    line: null,
    callback: null,
    message: null,
    asks: null,
    appointment: null,
    failure: null,
    shape: null,
    excerpt: null,
    unreadable: [],
  };
}

/**
 * Check facts field by field. NEVER THROWS, and never drops more than the
 * field that failed: each one that does not fit its schema is nulled and named
 * in `unreadable` (a field already named there stays named).
 *
 * Returns null only when there is nothing to read at all (`undefined` or
 * `null`): that is "this worker sent no facts", which the API must not confuse
 * with "facts, all empty". A value that is not an object is facts with every
 * field unreadable: something was sent, and it said nothing usable.
 *
 * Logs nothing. A caller that wants a line logs the field names in
 * `unreadable`, never the values, which are the caller's words.
 */
export function salvageCallFollowUpFacts(raw: unknown): CallFollowUpFacts | null {
  if (raw === undefined || raw === null) return null;
  const facts = emptyCallFollowUpFacts();
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return { ...facts, unreadable: [...CALL_FOLLOW_UP_FACT_FIELDS] };
  }
  const input = raw as Record<string, unknown>;
  const unreadable = new Set<CallFollowUpFactField>();
  const priorUnreadable = z.array(z.enum(CALL_FOLLOW_UP_FACT_FIELDS)).safeParse(input.unreadable);
  if (priorUnreadable.success) priorUnreadable.data.forEach((field) => unreadable.add(field));

  for (const field of CALL_FOLLOW_UP_FACT_FIELDS) {
    const value = input[field];
    // Absent reads as "no such fact", not as a malformed one.
    if (value === undefined || value === null) continue;
    const parsed = FIELD_SCHEMAS[field].safeParse(value);
    if (parsed.success) {
      (facts as Record<CallFollowUpFactField, unknown>)[field] = parsed.data;
    } else {
      unreadable.add(field);
    }
  }
  return { ...facts, unreadable: CALL_FOLLOW_UP_FACT_FIELDS.filter((f) => unreadable.has(f)) };
}
